import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Divider,
  Alert,
  Modal,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { ModalBox } from '@frontend/features/common/utils/modals';
import { TablePagination } from '@frontend/features/common/components/Table/TablePagination';
import { usePagination } from '@frontend/features/common/components/Table/usePagination';

import { useGetJobsQuery } from '../../queries';
import { JobAccordion } from './JobAccordion';
import { LoadingSkeleton } from './LoadingSkeleton';
import {
  modalBoxStyles,
  headerStyles,
  headerTitleContainerStyles,
  resultsContainerStyles,
  resultsTextStyles,
  emptyStateStyles,
} from './styles';

interface JobsModalProps {
  open: boolean;
  onClose: () => void;
}


export const JobsModal = ({ open, onClose }: JobsModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { page, rowsPerPage, handlePagination, handleRowsPerPage } = usePagination(10, 1, {
    keepPaginationInUrl: false
  });

  const { data: { jobs = [], total = 0 } = {}, isLoading, error } = useGetJobsQuery({
    page: page,
    limit: rowsPerPage
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="jobs-modal-title"
      sx={{
        overflow: 'auto',
      }}
    >
      <ModalBox
        display="flex"
        flexDirection="column"
        gap={theme.spacing(2)}
        sx={modalBoxStyles}
      >
        <Box sx={headerStyles}>
          <Box sx={headerTitleContainerStyles}>
            <Typography id="jobs-modal-title" variant="h6" component="h2">
              {t('hotelContent.jobs.modal.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon color="primary" />
          </IconButton>
        </Box>
        <Divider />

        <Box sx={resultsContainerStyles}>
          <Typography
            variant="subtitle1"
            align="left"
            color={theme.palette.text.primary}
            sx={resultsTextStyles}
          >
            {total} {t('hotelContent.jobs.modal.resultsFound')}
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {JSON.stringify(error, null, 2)}
          </Alert>
        ) : null}

        {isLoading && <LoadingSkeleton />}

        {!isLoading && (
          <>
            <Box>
              {jobs.length > 0 ? (
                jobs.map((job) => (
                  <JobAccordion key={job.id} job={job} />
                ))
              ) : (
                <Box sx={emptyStateStyles}>
                  <Typography variant="body1" color="text.secondary">
                    {t('hotelContent.jobs.modal.noJobsFound')}
                  </Typography>
                </Box>
              )}
            </Box>

            {total > 0 && (
              <TablePagination
                totalRows={total}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                onChangePage={handlePagination}
                onChangeRowsPerPage={handleRowsPerPage}
              />
            )}
          </>
        )}
      </ModalBox>
    </Modal>
  );
};
